import { api } from "./client";
import { apiRoutes } from "util/apiRoutes";
import type { LocationData } from "ui/LocationSearchField";

export interface PlaceRecord {
  id: number;
  google_place_id?: string | null;
  name: string;
  formatted_address?: string | null;
  locality?: string | null;
  region?: string | null;
  country_code?: string | null;
  lat?: number | null;
  lng?: number | null;
  place_type?: string | null;
  source?: "local" | "google";
  properties?: Record<string, unknown>;
}

export interface PlaceSearchResponse {
  items: PlaceRecord[];
  total: number;
  limit: number;
  offset: number;
}

export const placeApi = {
  search: async (query: string, options?: { limit?: number; offset?: number }): Promise<PlaceSearchResponse> => {
    const response = await api.get<PlaceSearchResponse>(apiRoutes.places.search, {
      params: {
        query,
        limit: options?.limit,
        offset: options?.offset,
      },
    });
    return response.data;
  },

  create: async (payload: Record<string, unknown>): Promise<PlaceRecord> => {
    const response = await api.post<PlaceRecord>(apiRoutes.places.root, payload);
    return response.data;
  },

  createFromLocationData: async (
    location: LocationData,
    options?: {
      audience?: string;
      properties?: Record<string, unknown>;
    }
  ): Promise<PlaceRecord> => {
    const fallbackName =
      (typeof location.name === "string" && location.name.trim().length > 0
        ? location.name
        : location.formatted_address.split(",")[0]) || location.formatted_address;

    return placeApi.create({
      google_place_id: location.placeId || null,
      name: fallbackName,
      formatted_address: location.formatted_address,
      locality: location.city || null,
      region: location.state || null,
      lat: location.lat,
      lng: location.lng,
      audience: options?.audience || "public",
      properties: {
        ...(options?.properties || {}),
        source: "google_places_autocomplete",
      },
    });
  },

  getById: async (id: number | string): Promise<PlaceRecord> => {
    const response = await api.get<PlaceRecord>(apiRoutes.places.byId(id));
    return response.data;
  },
};
